import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

type NpsStatus = "pending" | "submitted" | "dismissed";

interface NpsSurveyResponse {
  status: NpsStatus;
  score: number | null;
}

type Step = "score" | "followup";

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const followUpQuestion = (score: number): string => {
  if (score <= 6) {
    return "We're sorry to hear that. What's the main thing we could improve?";
  }
  if (score <= 8) {
    return "Thanks! What would make Bento a 10 for you?";
  }
  return "Love that! What do you enjoy most about Bento?";
};

export default function NpsSurvey() {
  const { getToken } = useAuth();
  const { currentOrg, totalEmailsSent } = useContext(OrganizationUserContext);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("score");
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const baseUrl = currentOrg?.id
    ? `/api/organization/${currentOrg.id}/nps-survey-responses`
    : null;

  useEffect(() => {
    const maybeShow = async () => {
      if (!baseUrl || !totalEmailsSent) {
        return;
      }
      const res: NpsSurveyResponse | null = await fetcherAuth(
        getToken,
        baseUrl,
      );
      if (res && res.status !== "pending") {
        return;
      }
      if (res?.score != null) {
        setScore(res.score);
        setStep("followup");
      }
      setOpen(true);
      trackEvent("NPS Survey Shown");
    };
    maybeShow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, totalEmailsSent]);

  const selectScore = (value: number) => {
    setScore(value);
    setStep("followup");
    if (baseUrl) {
      fetcherAuth(getToken, `${baseUrl}/score`, "POST", {}, { score: value });
    }
  };

  const submit = async () => {
    if (score == null || !baseUrl) {
      return;
    }
    setSubmitting(true);
    try {
      await fetcherAuth(
        getToken,
        `${baseUrl}/submit`,
        "POST",
        {},
        {
          score,
          feedback: feedback.trim() || null,
        },
      );
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    setOpen(false);
    if (baseUrl) {
      fetcherAuth(getToken, `${baseUrl}/dismiss`, "POST");
    }
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper elevation={6} sx={styles.root}>
        <IconButton size="small" onClick={dismiss} sx={styles.close}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={{ color: grey[500] }}
          />
        </IconButton>

        {step === "score" ? (
          <>
            <Typography sx={styles.question}>
              How likely are you to recommend Bento to a friend or fellow
              creator?
            </Typography>
            <Box sx={styles.scoreRow}>
              {SCORES.map((value) => (
                <Button
                  key={value}
                  onClick={() => selectScore(value)}
                  sx={styles.scoreButton}
                >
                  {value}
                </Button>
              ))}
            </Box>
            <Box sx={styles.scoreLabels}>
              <Typography sx={styles.scoreLabel}>Not likely</Typography>
              <Typography sx={styles.scoreLabel}>Very likely</Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography sx={styles.question}>
              {followUpQuestion(score ?? 0)}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Optional — a sentence is plenty"
              sx={styles.textField}
            />
            <Button
              variant="contained"
              onClick={submit}
              disabled={submitting}
              sx={styles.submit}
            >
              Submit feedback
            </Button>
          </>
        )}
      </Paper>
    </Slide>
  );
}

const styles = {
  root: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 1300,
    width: { xs: "calc(100% - 32px)", sm: 380 },
    p: 3,
    borderRadius: 3,
  },
  close: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  question: {
    fontWeight: 600,
    fontSize: "1rem",
    pr: 3,
    mb: 2,
  },
  scoreRow: {
    display: "flex",
    gap: 0.5,
  },
  scoreButton: {
    flex: 1,
    minWidth: 0,
    height: 30,
    px: 0,
    fontSize: "0.75rem",
    color: "text.primary",
    border: "1px solid",
    borderColor: "divider",
    "&:hover": {
      backgroundColor: "primary.main",
      color: "common.white",
      borderColor: "primary.main",
    },
  },
  scoreLabels: {
    display: "flex",
    justifyContent: "space-between",
    mt: 1,
  },
  scoreLabel: {
    fontSize: "0.75rem",
    color: "text.secondary",
  },
  textField: {
    mb: 2,
  },
  submit: {
    color: "common.white",
  },
} as const;
